import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/shared/Modal';
import { doc, getDoc } from 'firebase/firestore';
import { db, firestorePathId } from '../services/firebase';
import { ShieldCheck, CheckCircle2 } from 'lucide-react';

const AuthPage = () => {
    const [cpf, setCpf] = useState('');
    const [password, setPassword] = useState('');
    const [municipioId, setMunicipioId] = useState(null);
    const [portalDescription, setPortalDescription] = useState(''); 
    
    // Controle local do spinner
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    
    // Controle da Animação de Sucesso
    const [loginSuccess, setLoginSuccess] = useState(false);
    
    // Controle do Fade Out (Sumir devagar)
    const [isFadingOut, setIsFadingOut] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const { login, loading: contextLoading, isAuthenticated, authError, clearAuthError } = useAuth();

    // Detecta mensagem de logout na URL para uso no render (fora do useEffect)
    const queryParams = new URLSearchParams(location.search);
    const logoutMessage = queryParams.get('message');

    useEffect(() => {
        const fetchMunicipioData = async (id) => {
            if (!id) return;
            try {
                const docPath = `artifacts/${firestorePathId}/public/data/municipalities/${id}`;
                const municipioDocRef = doc(db, docPath);
                const docSnap = await getDoc(municipioDocRef);

                if (docSnap.exists() && docSnap.data().portalDescription) {
                    setPortalDescription(docSnap.data().portalDescription);
                }
            } catch (error) {
                console.error("Erro ao buscar descrição do município:", error);
            }
        };

        const idFromStorage = localStorage.getItem('municipioId');
        if (idFromStorage) {
            setMunicipioId(idFromStorage);
            fetchMunicipioData(idFromStorage);
        } else {
            // CORREÇÃO DO BUILD: Instanciamos URLSearchParams localmente aqui
            // Isso evita o erro de dependência 'queryParams' no linter
            const paramsFromUrl = new URLSearchParams(location.search);
            const idFromUrl = paramsFromUrl.get('municipioId');
            
            if (idFromUrl) {
                setMunicipioId(idFromUrl);
                localStorage.setItem('municipioId', idFromUrl);
                fetchMunicipioData(idFromUrl);
            }
        }
    }, [location.search]);
    
    // Redirecionamento Automático (Caso usuário já esteja logado ao entrar)
    useEffect(() => {
        if (isAuthenticated && !logoutMessage) {
            // Se já está logado e NÃO é logout, faz o fluxo de sucesso rápido
            setLoginSuccess(true);
            setTimeout(() => {
                navigate('/dashboard');
            }, 1000);
        }
    }, [isAuthenticated, navigate, logoutMessage]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        clearAuthError();
        setIsLoggingIn(true); // 1. Começa Radar

        // Delay mínimo para mostrar o radar girando
        await new Promise(r => setTimeout(r, 1000));

        if (!municipioId) {
            await login(cpf, password, null);
            setIsLoggingIn(false);
            return;
        }

        const result = await login(cpf, password, municipioId);
        
        if (result.success) {
            // 2. SUCESSO! Ativa ícone verde
            setLoginSuccess(true);

            // 3. Aguarda o usuário ler "Seja bem vindo" (1.5 segundos)
            await new Promise(r => setTimeout(r, 1500));

            // 4. Começa a sumir devagar (Fade Out)
            setIsFadingOut(true);

            // Aguarda a transição CSS terminar (ex: 500ms)
            await new Promise(r => setTimeout(r, 500));

            // 5. Navega
            if(result.needsVinculoSelection) {
                navigate('/selecionar-vinculo');
            } else if (result.mustChangePassword) {
                navigate('/change-password');
            } else {
                navigate('/dashboard');
            }
        } else {
            // ERRO: Destrava para tentar de novo
            setIsLoggingIn(false);
            setLoginSuccess(false);
        }
    };

    // LÓGICA DE EXIBIÇÃO DO OVERLAY
    // Só mostramos o loading do contexto se NÃO for um logout (evita aparecer "Aguarde" no logout)
    const showLoadingOverlay = (contextLoading && !logoutMessage) || isLoggingIn || loginSuccess;

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 relative">
            
            {/* --- OVERLAY COM TRANSIÇÃO SUAVE --- */}
            {showLoadingOverlay && (
                <div 
                    className={`fixed inset-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md z-[9999] flex flex-col items-center justify-center cursor-wait transition-opacity duration-500 ease-in-out ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}
                >
                    <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl flex flex-col items-center border border-gray-100 dark:border-gray-700 max-w-sm w-full mx-4 transform scale-105 transition-transform">
                        
                        <div className="relative w-24 h-24 mb-6 flex items-center justify-center">
                            {loginSuccess ? (
                                // --- ÍCONE VERDE (SUCESSO) ---
                                <div className="animate-bounce-in">
                                    <div className="absolute inset-0 bg-green-100 dark:bg-green-900/30 rounded-full animate-ping opacity-20"></div>
                                    <CheckCircle2 size={64} className="text-green-500 dark:text-green-400 relative z-10" />
                                </div>
                            ) : (
                                // --- ÍCONE RADAR (CARREGANDO) ---
                                <>
                                    <div className="absolute inset-0 border-4 border-indigo-500/20 rounded-full animate-ping"></div>
                                    <div className="absolute inset-0 border-4 border-transparent border-t-indigo-600 rounded-full animate-spin"></div>
                                    <div className="absolute inset-0 border-4 border-slate-100 dark:border-slate-700 rounded-full opacity-30"></div>
                                    <div className="relative z-10 bg-white dark:bg-gray-800 rounded-full p-2">
                                        <ShieldCheck size={32} className="text-indigo-600 dark:text-indigo-400" />
                                    </div>
                                </>
                            )}
                        </div>
                        
                        {/* MENSAGENS DINÂMICAS */}
                        <h3 className={`text-xl font-bold mb-2 text-center transition-colors duration-300 ${loginSuccess ? 'text-green-600 dark:text-green-400' : 'text-gray-800 dark:text-white'}`}>
                            {loginSuccess ? 'Seja bem-vindo!' : 'Autenticando...'}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                            {loginSuccess 
                                ? 'Acesso autorizado. Carregando sistema...' 
                                : 'Validando credenciais criptografadas.'}
                        </p>
                    </div>
                </div>
            )}

            <div className="w-full max-w-md p-8 space-y-8 bg-white dark:bg-gray-800 rounded-lg shadow-md z-10">
                <div className="text-center">
                    <img
                        src="https://firebasestorage.googleapis.com/v0/b/portal-sisfol.firebasestorage.app/o/LOGOSISRH%2FSERVIDOR%2FSISRH%20-%20servidor.png?alt=media&token=517262c7-d4ea-4d02-b0f0-2799e6158542"
                        alt="Logo Portal do Servidor"
                        className="mx-auto h-auto w-48 mb-4"
                    />
                    
                    {portalDescription && (
                        <p className="mt-4 text-md text-gray-700 dark:text-gray-300">
                            {portalDescription}
                        </p>
                    )}

                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Acesse com seu CPF e senha.
                    </p>
                </div>
                
                <form className="space-y-6" onSubmit={handleSubmit}>
                    <Input
                        id="cpf"
                        label="CPF"
                        type="text"
                        value={cpf}
                        onChange={(e) => setCpf(e.target.value)}
                        placeholder="Digite seu CPF"
                        required
                        disabled={showLoadingOverlay}
                    />
                    <Input
                        id="password"
                        label="Senha"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Sua senha"
                        required
                        disabled={showLoadingOverlay}
                    />

                    <Button type="submit" disabled={showLoadingOverlay || !municipioId}>
                        Entrar
                    </Button>
                </form>
            </div>

            {/* Modal de Erro ou Mensagem de Logout */}
            <Modal 
                isOpen={!!authError || (!!logoutMessage && !isLoggingIn && !loginSuccess)}
                onClose={() => {
                    clearAuthError();
                    // Limpa a URL se for mensagem de logout
                    if (logoutMessage) navigate('/login', { replace: true });
                }}
                title={authError ? "Atenção" : "Até a próxima!"}
            >
                <p className="text-center text-gray-700 dark:text-gray-300">
                    {authError || logoutMessage || "Sessão encerrada com segurança."}
                </p>
                <div className="mt-4 flex justify-center">
                    <Button onClick={() => {
                        clearAuthError();
                        if (logoutMessage) navigate('/login', { replace: true });
                    }}>
                        Fechar
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default AuthPage;